// PII scrubbing + env helpers for PostHog. Framework-agnostic so it can be shared
// across browser and server-side capture paths.
import type { CaptureResult } from 'posthog-js';
import { scrubObject, resolveEnvironment } from '@attik/posthog';

// Re-export so callers that import scrubObject from here still work.
export { scrubObject } from '@attik/posthog';

/** Share of `$web_vitals` events kept. Enough to chart percentiles, at a tenth of the volume. */
export const WEB_VITALS_SAMPLE_RATE = 0.1;

// React hydration mismatches (#418 text, #423 recovery, #425 text content).
// #418 alone was ~64% of our error volume and repeats on every render of the
// affected pages, so only the first one per tab session is sent: enough to
// count affected sessions without spending the error allowance on repeats.
const HYDRATION_ERROR = /Minified React error #(418|423|425)\b/;
const HYDRATION_SENT_KEY = 'attik_ph_hydration_error_sent';

function isRepeatHydrationError(event: CaptureResult): boolean {
  const list = event.properties?.$exception_list as Array<{ value?: unknown }> | undefined;
  if (!list?.some((e) => typeof e.value === 'string' && HYDRATION_ERROR.test(e.value))) {
    return false;
  }
  try {
    if (sessionStorage.getItem(HYDRATION_SENT_KEY)) return true;
    sessionStorage.setItem(HYDRATION_SENT_KEY, '1');
  } catch {
    // storage blocked — send it
  }
  return false;
}

/**
 * Samples `$web_vitals`, throttles repeat hydration errors, and redacts PII
 * from PostHog exception events before they leave the browser.
 */
export function posthogBeforeSend(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  if (event.event === '$web_vitals' && Math.random() >= WEB_VITALS_SAMPLE_RATE) {
    return null;
  }
  if (event.event === '$exception' && isRepeatHydrationError(event)) {
    return null;
  }
  try {
    if (event.event === '$exception' && event.properties) {
      // Scrub only custom properties; never touch PostHog internals like
      // $exception_list / $current_url, so the exception payload stays intact.
      const props = event.properties as Record<string, unknown>;
      for (const key of Object.keys(props)) {
        if (key.startsWith('$')) continue;
        props[key] = scrubObject(props[key]);
      }
    }
  } catch {
    // Never drop an event because scrubbing failed.
  }
  return event;
}

export function posthogEnvironment(): string {
  return resolveEnvironment(
    process.env.NEXT_PUBLIC_POSTHOG_ENVIRONMENT,
    process.env.NEXT_PUBLIC_VERCEL_ENV,
    process.env.NODE_ENV,
  );
}

export function posthogRelease(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_POSTHOG_RELEASE ||
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
    undefined
  );
}

/** Parses the PostHog `distinct_id` from a request Cookie header (server-side). */
export function distinctIdFromCookieHeader(
  cookieHeader: string | null | undefined
): string | undefined {
  if (!cookieHeader) return undefined;
  for (const part of cookieHeader.split(';')) {
    const idx = part.indexOf('=');
    if (idx === -1) continue;
    const name = part.slice(0, idx).trim();
    if (/^ph_.*_posthog$/.test(name)) {
      try {
        const parsed = JSON.parse(decodeURIComponent(part.slice(idx + 1).trim()));
        if (parsed && typeof parsed.distinct_id === 'string') return parsed.distinct_id;
      } catch {
        // ignore malformed cookie
      }
    }
  }
  return undefined;
}
